import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase/client";

export interface User {
  id: string;
  email: string;
  role: string;
  created_at: string;
  last_sign_in_at?: string;
  email_confirmed_at?: string;
  company_name?: string;
  company_id?: string;
}

export interface UserSearchResult {
  users: User[];
  totalCount: number;
  hasMore: boolean;
}

interface SupabaseRPCUser {
  id: string;
  email?: string;
  role?: string;
  created_at: string;
  last_sign_in_at?: string;
  email_confirmed_at?: string;
}

export function useUserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const pageSize = 20;

  const fetchUsers = useCallback(async (page = 0, reset = true) => {
    setLoading(true);
    setError(null);
    
    try {
      // First, get users from the users table
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, email, role, created_at, company_id')
        .range(page * pageSize, (page + 1) * pageSize - 1)
        .order('created_at', { ascending: false });

      let formattedUsers: User[] = [];

      if (usersError) {
        console.log('Direct users query failed, falling back to RPC:', usersError);
        // Fallback to RPC if direct query fails
        const { data, error } = await supabase.rpc('get_all_users', {
          page_limit: pageSize,
          page_offset: page * pageSize
        });

        if (error) throw error;

        formattedUsers = data.map((user: SupabaseRPCUser) => ({
          id: user.id,
          email: user.email || '',
          role: user.role || 'user',
          created_at: user.created_at,
          last_sign_in_at: user.last_sign_in_at,
          email_confirmed_at: user.email_confirmed_at,
          company_id: null,
          company_name: null
        }));
      } else {
        // Process users data
        formattedUsers = usersData.map((user) => ({
          id: user.id,
          email: user.email || '',
          role: user.role || 'user',
          created_at: user.created_at,
          company_id: user.company_id,
          company_name: null // Will be filled in next step
        }));

        // Get unique company IDs
        const companyIds = [...new Set(usersData
          .map(u => u.company_id)
          .filter(id => id != null))];

        // Fetch company names if there are any company IDs
        if (companyIds.length > 0) {
          const { data: companiesData, error: companiesError } = await supabase
            .from('companies')
            .select('id, name')
            .in('id', companyIds);

          if (!companiesError && companiesData) {
            const companyMap = new Map(companiesData.map(c => [c.id, c.name]));
            formattedUsers.forEach(user => {
              if (user.company_id) {
                user.company_name = companyMap.get(user.company_id) || null;
              }
            });
          } else {
            console.log('Could not fetch company names:', companiesError);
          }
        }

        // Also fetch last_sign_in_at from auth.users if we have admin access
        try {
          const { data: authData } = await supabase.rpc('get_all_users', {
            page_limit: pageSize,
            page_offset: page * pageSize
          });
          
          if (authData) {
            const authMap = new Map(authData.map((u: SupabaseRPCUser) => [u.id, u]));
            formattedUsers.forEach(user => {
              const authUser = authMap.get(user.id);
              if (authUser) {
                user.last_sign_in_at = authUser.last_sign_in_at;
                user.email_confirmed_at = authUser.email_confirmed_at;
              }
            });
          }
        } catch (authErr) {
          console.log('Could not fetch auth data:', authErr);
        }
      }

      if (reset) {
        setUsers(formattedUsers);
      } else {
        setUsers(prev => [...prev, ...formattedUsers]);
      }
      
      setCurrentPage(page);
      setTotalUsers(formattedUsers.length); // Note: This is approximate since we're paginating
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err as Error);
      
      // Fallback to session-based approach
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          const currentUser: User = {
            id: sessionData.session.user.id,
            email: sessionData.session.user.email || '',
            role: sessionData.session.user.user_metadata?.role || 'user',
            created_at: sessionData.session.user.created_at || new Date().toISOString(),
            last_sign_in_at: sessionData.session.user.last_sign_in_at,
            email_confirmed_at: sessionData.session.user.email_confirmed_at
          };
          setUsers([currentUser]);
        }
      } catch (fallbackErr) {
        console.error("Fallback approach failed:", fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const searchUsers = useCallback(async (term: string) => {
    if (!term.trim()) {
      setSearchTerm("");
      setIsSearching(false);
      fetchUsers(0, true);
      return;
    }

    setIsSearching(true);
    setSearchTerm(term);
    setError(null);
    
    try {
      // First try direct search with company data
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, email, role, created_at, company_id')
        .ilike('email', `%${term}%`)
        .limit(pageSize);

      let formattedUsers: User[] = [];

      if (usersError) {
        console.log('Direct search failed, using RPC:', usersError);
        // Fallback to RPC search
        const { data, error } = await supabase.rpc('search_users_by_email', {
          search_term: term,
          page_limit: pageSize
        });

        if (error) throw error;

        formattedUsers = data.map((user: SupabaseRPCUser) => ({
          id: user.id,
          email: user.email || '',
          role: user.role || 'user',
          created_at: user.created_at,
          last_sign_in_at: user.last_sign_in_at,
          company_id: null,
          company_name: null
        }));
      } else {
        // Process users data
        formattedUsers = usersData.map((user) => ({
          id: user.id,
          email: user.email || '',
          role: user.role || 'user',
          created_at: user.created_at,
          company_id: user.company_id,
          company_name: null
        }));

        // Get unique company IDs
        const companyIds = [...new Set(usersData
          .map(u => u.company_id)
          .filter(id => id != null))];

        // Fetch company names if there are any company IDs
        if (companyIds.length > 0) {
          const { data: companiesData } = await supabase
            .from('companies')
            .select('id, name')
            .in('id', companyIds);

          if (companiesData) {
            const companyMap = new Map(companiesData.map(c => [c.id, c.name]));
            formattedUsers.forEach(user => {
              if (user.company_id) {
                user.company_name = companyMap.get(user.company_id) || null;
              }
            });
          }
        }
      }

      setUsers(formattedUsers);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsSearching(false);
    }
  }, [pageSize, fetchUsers]);

  const updateUserRole = async (userId: string, newRole: string): Promise<{success: boolean, error?: string}> => {
    setError(null);
    
    try {
      const { data, error } = await supabase.rpc('update_user_role', {
        target_user_id: userId,
        new_role: newRole
      });

      if (error) throw error;

      // Handle case where data might be null/undefined
      if (!data) {
        // If no data returned but no error, assume success (some RPC functions don't return data)
        // Update local state optimistically
        setUsers(prev => prev.map(user => 
          user.id === userId ? { ...user, role: newRole } : user
        ));
        return { success: true };
      }

      // Check if the response indicates success
      if (data.success) {
        // Update local state
        setUsers(prev => prev.map(user => 
          user.id === userId ? { ...user, role: newRole } : user
        ));
        return { success: true };
      } else {
        const errorMsg = data.error || 'Unknown error occurred';
        setError(new Error(errorMsg));
        return { success: false, error: errorMsg };
      }
    } catch (err) {
      const errorMsg = (err as Error).message;
      setError(err as Error);
      return { success: false, error: errorMsg };
    }
  };

  const loadMoreUsers = () => {
    if (!isSearching && !loading) {
      fetchUsers(currentPage + 1, false);
    }
  };

  const refreshUsers = () => {
    setCurrentPage(0);
    if (searchTerm) {
      searchUsers(searchTerm);
    } else {
      fetchUsers(0, true);
    }
  };

  useEffect(() => {
    fetchUsers(0, true);
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    totalUsers,
    currentPage,
    searchTerm,
    isSearching,
    refetch: refreshUsers,
    updateUserRole,
    searchUsers,
    loadMoreUsers,
    hasMore: users.length >= pageSize * (currentPage + 1)
  };
}
